// Virtual needle: plays the *geometry* back as audio. It knows nothing about the source audio. It only
// follows the groove polyline the cutter would follow, at the turntable speed, and records how far the
// groove deviates from a plain spiral (laser) or how deep the floor is (3D print).
//
// Straight segments between vertices are interpolated linearly, because that's what a cutter actually
// produces between two path points.

import { differentiate, highpass, normalize, removeDC, riaaPlayback } from "../dsp/filters";
import type { Track } from "../geom/types";

export interface NeedleOptions {
  sampleRate?: number;
  cartridge?: "ceramic" | "magnetic";
  /** Snap x/y to this grid (inches) before reading, e.g. 1/dpi. 0 = off. */
  quantize?: number;
  /** Snap z (3D) to this grid (inches), e.g. one print layer. 0 = off. */
  zQuantize?: number;
}

export interface NeedleOutput {
  /** Mono signal for laser grooves; for 3D, L and R (vertical motion = anti-phase L/R). */
  channels: Float32Array[];
  sampleRate: number;
  seconds: number;
}

const q = (x: number, step: number) => (step > 0 ? Math.round(x / step) * step : x);

/** Turn (time, value) pairs with increasing time into a uniformly sampled signal. */
function toUniform(t: Float64Array, d: Float64Array, n: number, fs: number): Float32Array {
  const T = n > 0 ? t[n - 1] : 0;
  const out = new Float32Array(Math.max(1, Math.floor(T * fs)));
  let k = 0;
  for (let s = 0; s < out.length; s++) {
    const ts = s / fs;
    while (k < n - 2 && t[k + 1] < ts) k++;
    const dt = t[k + 1] - t[k];
    const a = dt > 0 ? (ts - t[k]) / dt : 0;
    out[s] = d[k] + (d[k + 1] - d[k]) * Math.min(1, Math.max(0, a));
  }
  return out;
}

/** Follow the track and return (time, deviation) samples. */
export interface TrackReading {
  t: Float64Array;
  d: Float64Array;
  n: number;
  /** For polar tracks: source vertex index of each reading (for drawing a playhead). */
  idx?: Int32Array;
}

export function readTrack(track: Track, quantize = 0, zQuantize = 0): TrackReading {
  if (track.kind === "linear") {
    let total = 0;
    for (const r of track.rows) total += r.length / 2;
    const t = new Float64Array(total), d = new Float64Array(total);
    let n = 0, tOff = 0;
    track.rows.forEach((row, ri) => {
      const x0 = row[0];
      let last = -Infinity;
      for (let i = 0; i < row.length; i += 2) {
        const ti = tOff + (q(row[i], quantize) - x0) / track.speed;
        if (ti <= last) continue;
        t[n] = ti;
        d[n] = q(row[i + 1], quantize) - track.centers[ri];
        last = ti;
        n++;
      }
      const xEnd = row[row.length - 2];
      tOff += (xEnd - x0) / track.speed + 1e-6;
    });
    return { t, d, n };
  }
  const stride = track.kind === "polar" ? 2 : 3;
  const pts = track.kind === "polar" ? track.xy : track.xyz;
  const m = pts.length / stride;
  const t = new Float64Array(m), d = new Float64Array(m), idx = new Int32Array(m);
  const secPerRad = 60 / track.rpm / (2 * Math.PI);
  let n = 0;
  let prevA = 0, unwrapped = 0, sign = 0, lastT = -Infinity;
  for (let i = 0; i < m; i++) {
    const x = q(pts[i * stride], quantize), y = q(pts[i * stride + 1], quantize);
    const a = Math.atan2(y - track.cy, x - track.cx);
    if (i > 0) {
      let da = a - prevA;
      if (da > Math.PI) da -= 2 * Math.PI;
      else if (da < -Math.PI) da += 2 * Math.PI;
      if (sign === 0 && Math.abs(da) > 1e-9) sign = Math.sign(da);
      unwrapped += da * (sign || 1);
    }
    prevA = a;
    const ti = unwrapped * secPerRad;
    if (ti <= lastT) continue; // duplicate vertex (file split) or a backwards step
    lastT = ti;
    t[n] = ti;
    idx[n] = i;
    if (track.kind === "polar") {
      const rNominal = track.r0 - (track.pitch * unwrapped) / (2 * Math.PI);
      d[n] = Math.hypot(x - track.cx, y - track.cy) - rNominal;
    } else {
      d[n] = q(pts[i * stride + 2], zQuantize);
    }
    n++;
  }
  return { t, d, n, idx };
}

export function playTrack(track: Track, opts: NeedleOptions = {}): NeedleOutput {
  const fs = opts.sampleRate ?? 44100;
  const { t, d, n } = readTrack(track, opts.quantize ?? 0, opts.zQuantize ?? 0);
  if (n < 2) return { channels: [new Float32Array(1)], sampleRate: fs, seconds: 0 };
  let x = toUniform(t, d, n, fs);
  removeDC(x);
  if (opts.cartridge === "magnetic") {
    // stylus velocity → RIAA de-emphasis in the phono preamp
    differentiate(x, fs);
    riaaPlayback(fs).process(x);
  }
  highpass(20, fs).process(x);
  highpass(20, fs).process(x);
  normalize(x, 0.9);
  const seconds = x.length / fs;
  if (track.kind === "polar3d") {
    // A stereo cartridge reads vertical motion as L−R: the channels are in anti-phase.
    const R = new Float32Array(x.length);
    for (let i = 0; i < x.length; i++) R[i] = -x[i];
    return { channels: [x, R], sampleRate: fs, seconds };
  }
  return { channels: [x], sampleRate: fs, seconds };
}
